import React, { useState, useEffect } from 'react';
import { Clock, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { SchoolTime, TimeSlot, DaySchedule, BreakTime } from '../../types/schedule';
import {
    getSchoolTimes,
    addSchoolTime,
    updateSchoolTime,
    deleteSchoolTime
} from '../../utils/schedule';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

const SchoolTimeConfig: React.FC = () => {
    const { showSuccess, showError } = useToast();

    // State
    const [schoolTimes, setSchoolTimes] = useState<SchoolTime[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTime, setEditingTime] = useState<SchoolTime | null>(null);
    const [formData, setFormData] = useState({
        name: 'School Schedule',
        schoolStartTime: '08:00',
        schoolEndTime: '15:00',
        days: [
            { day: 'Monday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Tuesday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Wednesday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Thursday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Friday', isActive: true, startTime: '08:00', endTime: '12:00', breakTimes: [] }, // Friday half day
            { day: 'Saturday', isActive: false, startTime: '08:00', endTime: '15:00', breakTimes: [] },
            { day: 'Sunday', isActive: false, startTime: '08:00', endTime: '15:00', breakTimes: [] }
        ] as DaySchedule[],
        isActive: true
    });

    useEffect(() => {
        fetchSchoolTimes();
    }, []);

    const fetchSchoolTimes = async () => {
        setLoading(true);
        try {
            const data = await getSchoolTimes();
            setSchoolTimes(data);
        } catch (error) {
            showError('Error', 'Failed to fetch school times');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Check if at least one day is active
        const activeDays = formData.days.filter(day => day.isActive);
        if (activeDays.length === 0) {
            showError('Error', 'Please enable at least one day');
            return;
        }

        try {
            if (editingTime) {
                await updateSchoolTime(editingTime.id, formData);
                showSuccess('Updated', 'School time configuration updated successfully');
            } else {
                // Check if there's already an existing school time configuration
                if (schoolTimes.length > 0) {
                    const shouldReplace = window.confirm(
                        'A school time configuration already exists. Do you want to replace it with this new one? The existing configuration will be deleted.'
                    );

                    if (!shouldReplace) {
                        return; // User cancelled
                    }

                    // Delete the existing configuration
                    await deleteSchoolTime(schoolTimes[0].id);
                }

                await addSchoolTime(formData);
                showSuccess('Created', 'School time configuration created successfully');
            }

            setShowForm(false);
            setEditingTime(null);
            resetForm();
            fetchSchoolTimes();
        } catch (error) {
            showError('Error', 'Failed to save school time configuration');
        }
    };

    const handleEdit = (schoolTime: SchoolTime) => {
        setEditingTime(schoolTime);
        setFormData({
            name: schoolTime.name,
            schoolStartTime: schoolTime.schoolStartTime,
            schoolEndTime: schoolTime.schoolEndTime,
            days: schoolTime.days.map(day => ({
                ...day,
                breakTimes: day.breakTimes || []
            })),
            isActive: schoolTime.isActive
        });
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this school time configuration?')) {
            try {
                await deleteSchoolTime(id);
                showSuccess('Deleted', 'School time configuration deleted successfully');
                fetchSchoolTimes();
            } catch (error) {
                showError('Error', 'Failed to delete school time configuration');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: 'School Schedule',
            schoolStartTime: '08:00',
            schoolEndTime: '15:00',
            days: [
                { day: 'Monday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
                { day: 'Tuesday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
                { day: 'Wednesday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
                { day: 'Thursday', isActive: true, startTime: '08:00', endTime: '15:00', breakTimes: [] },
                { day: 'Friday', isActive: true, startTime: '08:00', endTime: '12:00', breakTimes: [] }, // Friday half day
                { day: 'Saturday', isActive: false, startTime: '08:00', endTime: '15:00', breakTimes: [] },
                { day: 'Sunday', isActive: false, startTime: '08:00', endTime: '15:00', breakTimes: [] }
            ],
            isActive: true
        });
    };


    const toggleDay = (dayIndex: number) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? { ...day, isActive: !day.isActive } : day
            )
        }));
    };

    const updateDayTime = (dayIndex: number, field: 'startTime' | 'endTime', value: string) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? { ...day, [field]: value } : day
            )
        }));
    };

    const addBreakTime = (dayIndex: number) => {
        const newBreak: BreakTime = {
            id: Date.now().toString(),
            name: 'Break',
            startTime: '10:00',
            endTime: '10:15',
            duration: 15
        };
        setFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? { ...day, breakTimes: [...day.breakTimes, newBreak] } : day
            )
        }));
    };

    const updateBreakTime = (dayIndex: number, breakIndex: number, field: keyof BreakTime, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? {
                    ...day,
                    breakTimes: day.breakTimes.map((breakTime, j) =>
                        j === breakIndex ? { ...breakTime, [field]: value } : breakTime
                    )
                } : day
            )
        }));
    };

    const removeBreakTime = (dayIndex: number, breakIndex: number) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? {
                    ...day,
                    breakTimes: day.breakTimes.filter((_, j) => j !== breakIndex)
                } : day
            )
        }));
    };

    const calculateBreakDuration = (startTime: string, endTime: string): number => {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        return endMinutes - startMinutes;
    };

    const handleBreakTimeChange = (dayIndex: number, breakIndex: number, field: 'startTime' | 'endTime', value: string) => {
        updateBreakTime(dayIndex, breakIndex, field, value);
        const breakTime = formData.days[dayIndex].breakTimes[breakIndex];
        const newBreakTime = { ...breakTime, [field]: value };
        const duration = calculateBreakDuration(newBreakTime.startTime, newBreakTime.endTime);
        updateBreakTime(dayIndex, breakIndex, 'duration', duration);
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">School Time Configuration</h3>
                    <p className="text-sm text-gray-600">Configure school timings and active days (only one configuration allowed)</p>
                </div>
                <Button onClick={() => setShowForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    {schoolTimes.length > 0 ? 'Replace Configuration' : 'Add Configuration'}
                </Button>
            </div>

            {/* School Times List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {schoolTimes.map((schoolTime) => (
                    <Card key={schoolTime.id} glassy={true} className="islamic-pattern">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Clock className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{schoolTime.name}</h4>
                                        <p className="text-sm text-gray-600">
                                            {schoolTime.schoolStartTime} - {schoolTime.schoolEndTime}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex space-x-1">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleEdit(schoolTime)}
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleDelete(schoolTime.id)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <h5 className="text-sm font-medium text-gray-700">Active Days:</h5>
                                {schoolTime.days.filter(day => day.isActive).map((day, index) => (
                                    <div key={day.day} className="p-2 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-between text-sm mb-1">
                                            <span className="text-gray-600 font-medium">{day.day}</span>
                                            <span className="text-gray-900">
                                                {day.startTime} - {day.endTime}
                                            </span>
                                        </div>
                                        {day.breakTimes && day.breakTimes.length > 0 && (
                                            <div className="mt-1">
                                                <div className="text-xs text-gray-500 mb-1">Break Times:</div>
                                                <div className="flex flex-wrap gap-1">
                                                    {day.breakTimes.map(breakTime => (
                                                        <span
                                                            key={breakTime.id}
                                                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700"
                                                        >
                                                            {breakTime.name}: {breakTime.startTime}-{breakTime.endTime}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Form Modal */}
            {showForm && (
                <Modal
                    isOpen={true}
                    onClose={() => {
                        setShowForm(false);
                        setEditingTime(null);
                        resetForm();
                    }}
                    title={editingTime ? 'Edit School Time' : (schoolTimes.length > 0 ? 'Replace School Time Configuration' : 'Add School Time Configuration')}
                    size="lg"
                >
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Warning for replacement */}
                        {!editingTime && schoolTimes.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-yellow-800">
                                            Replacing Existing Configuration
                                        </h3>
                                        <div className="mt-2 text-sm text-yellow-700">
                                            <p>An existing school time configuration will be deleted and replaced with this new one.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    School Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    placeholder="e.g., ABC School"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    School Start Time
                                </label>
                                <input
                                    type="time"
                                    value={formData.schoolStartTime}
                                    onChange={(e) => setFormData(prev => ({ ...prev, schoolStartTime: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    School End Time
                                </label>
                                <input
                                    type="time"
                                    value={formData.schoolEndTime}
                                    onChange={(e) => setFormData(prev => ({ ...prev, schoolEndTime: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    required
                                />
                            </div>
                        </div>


                        {/* Days of Week */}
                        <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Days of Week</h4>
                            <p className="text-sm text-gray-600 mb-4">Select which days the school is open, set timing, and configure break times for each day.</p>

                            <div className="space-y-4">
                                {formData.days.map((day, dayIndex) => (
                                    <div key={day.day} className="p-4 border border-gray-200 rounded-lg">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                <input
                                                    type="checkbox"
                                                    checked={day.isActive}
                                                    onChange={() => toggleDay(dayIndex)}
                                                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                                                />
                                                <h5 className="font-medium text-gray-900">{day.day}</h5>
                                            </div>
                                            {day.isActive && (
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="time"
                                                        value={day.startTime}
                                                        onChange={(e) => updateDayTime(dayIndex, 'startTime', e.target.value)}
                                                        className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                    <span className="text-gray-500">to</span>
                                                    <input
                                                        type="time"
                                                        value={day.endTime}
                                                        onChange={(e) => updateDayTime(dayIndex, 'endTime', e.target.value)}
                                                        className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {day.isActive && (
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <h6 className="text-sm font-medium text-gray-700">Break Times</h6>
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => addBreakTime(dayIndex)}
                                                    >
                                                        <Plus className="h-3 w-3 mr-1" />
                                                        Add Break
                                                    </Button>
                                                </div>

                                                <div className="space-y-2">
                                                    {day.breakTimes.map((breakTime, breakIndex) => (
                                                        <div key={breakTime.id} className="p-3 bg-gray-50 rounded-lg">
                                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                                                <div>
                                                                    <input
                                                                        type="text"
                                                                        value={breakTime.name}
                                                                        onChange={(e) => updateBreakTime(dayIndex, breakIndex, 'name', e.target.value)}
                                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                                                        placeholder="Break name"
                                                                        required
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <input
                                                                        type="time"
                                                                        value={breakTime.startTime}
                                                                        onChange={(e) => handleBreakTimeChange(dayIndex, breakIndex, 'startTime', e.target.value)}
                                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                                                        required
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <input
                                                                        type="time"
                                                                        value={breakTime.endTime}
                                                                        onChange={(e) => handleBreakTimeChange(dayIndex, breakIndex, 'endTime', e.target.value)}
                                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                                                        required
                                                                    />
                                                                </div>
                                                                <div className="flex items-center">
                                                                    <Button
                                                                        type="button"
                                                                        variant="secondary"
                                                                        size="sm"
                                                                        onClick={() => removeBreakTime(dayIndex, breakIndex)}
                                                                        className="w-full"
                                                                    >
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                            <div className="mt-1 text-xs text-gray-500">
                                                                Duration: {breakTime.duration} minutes
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setShowForm(false);
                                    setEditingTime(null);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="submit">
                                <Save className="h-4 w-4 mr-2" />
                                {editingTime ? 'Update' : 'Create'}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
};

export default SchoolTimeConfig;
